import { useEffect, useState } from 'react';
import { FaNode, FaReact, FaReacteurope } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Center, Heading, Icon, Image, useToast } from '@chakra-ui/react';
import {
  browserPopupRedirectResolver,
  getAuth,
  GoogleAuthProvider,
  onAuthStateChanged,
  signInWithPopup,
} from 'firebase/auth';
import { app } from '../../../../configs/firebaseApp';
import { useAppDispatch } from '../../../../redux/hooks';
import { setUserType } from '../../../../redux/user';
import { loginClient } from '../../../../redux/user/actions';
import { pxToRem } from '../../../../utils';
//@ts-ignore
import FlutterIcon from '../../assets/flutter.svg';
//@ts-ignore
import SolanaIcon from '../../assets/solana.svg';
//@ts-ignore
import SolidityIcon from '../../assets/solidity.svg';
import { Container } from '../container/container.component';
import { CodeBlock } from './code-block';

export const IntegrateScreen = () => {
  const techIcons = [
    { name: 'React', icon: FaReact },
    { name: 'React Native', icon: FaReacteurope },
    { name: 'Flutter', icon: FlutterIcon },
    { name: 'Node', icon: FaNode },
    { name: 'Solidity', icon: SolidityIcon },
    { name: 'Solana', icon: SolanaIcon },
  ];
  const [selectedTech, setSelectedTech] = useState('React');

  const handleTechClick = (tech: any) => {
    setSelectedTech(tech);
  };

  const getCodeSnippet = () => {
    switch (selectedTech) {
      case 'React':
        return `
      import React, { useState, useEffect } from 'react'
      import { ReclaimProofRequest } from '@reclaimprotocol/js-sdk'
 
      function ReclaimDemo() {
        const [requestUrl, setRequestUrl] = useState('')
        const [proofs, setProofs] = useState(null)
      
        useEffect(() => {
          async function initializeReclaim() {
            const APP_ID = 'YOUR_APPLICATION_ID'
            const APP_SECRET = 'YOUR_APPLICATION_SECRET'
            const PROVIDER_ID = 'YOUR_PROVIDER_ID'
      
            const reclaimProofRequest = await ReclaimProofRequest.init(APP_ID, APP_SECRET, PROVIDER_ID)
            
            const url = await reclaimProofRequest.getRequestUrl()
            setRequestUrl(url)
      
            await reclaimProofRequest.startSession({
              onSuccessCallback: (proofs) => {
                console.log('Verification success', proofs)
                setProofs(proofs)
              },
              onFailureCallback: (error) => {
                console.error('Verification failed', error)
              }
            })
          }
      
          initializeReclaim()
        }, [])
  
        return (
          <div>
            <h1>Reclaim Protocol Demo</h1>
            {requestUrl && (
              <div>
                <p>Request URL: {requestUrl}</p>
                <p>Use this URL to start the verification process</p>
              </div>
            )}
            {proofs && (
              <div>
                <h2>Verification Successful!</h2>
                <pre>{JSON.stringify(proofs, null, 2)}</pre>
              </div>
            )}
          </div>
        )
      }
      
      export default ReclaimDemo
      
     
`;

      case 'React Native':
        return `
      import { Reclaim } from '@reclaimprotocol/reactnative-sdk'
      import { SafeAreaView, Text, View, Pressable } from 'react-native'
 
      function App() {
      
        const APP_ID = 'YOUR_APPLICATION_ID_HERE'
        const reclaimClient = new Reclaim.ProofRequest(APP_ID)

        async function startVerificationFlow() {
        const providerId = 'provider_id' 
    
        const appDeepLink = 'YOUR_APP_DEEP_LINK_HERE' 
        reclaimClient.setAppCallbackUrl(appDeepLink)
        
        await reclaimClient.buildProofRequest(providerId)
 
        reclaimClient.setSignature(await reclaimClient.generateSignature(APP_SECRET))
 
        const { requestUrl, statusUrl } =
        await reclaimClient.createVerificationRequest(providerIds)
 
        await reclaimClient.startSession({
          onSuccessCallback: proof => {
           console.log('Verification success', proof)
           // Your business logic here
        },
        onFailureCallback: error => {
        console.error('Verification failed', error)
          // Your business logic here to handle the error
          }
        })
      }
 
      return (
        <SafeAreaView>
             <View>
           <Pressable onPress={startVerificationFlow}>
             <Text>Start Verification Flow</Text>
           </Pressable>
         </View>
       </SafeAreaView>
      )
}
      
      
      `;

      case 'Flutter':
        return `
      import 'package:flutter/material.dart';
      import 'package:reclaim_sdk/reclaim.dart';
      import 'package:reclaim_sdk/utils/interfaces.dart';
      import 'package:url_launcher/url_launcher.dart';
      
      Future<void> main() async {
        WidgetsFlutterBinding.ensureInitialized();
        runApp(MaterialApp(home: ReclaimExample()));
      }
      
      class ReclaimExample extends StatefulWidget {
        @override
        _ReclaimExampleState createState() => _ReclaimExampleState();
      }
      
      class _ReclaimExampleState extends State<ReclaimExample> {
        String _status = '';
        String _proofData = '';
      
        Future<void> _startVerificationSession(ReclaimProofRequest request) async {
          await request.startSession(
            onSuccess: _handleProofSuccess,
            onError: _handleProofError,
          );
        }
      
        void _handleProofSuccess(Proof proof) {
          print('Proof received: $proof');
          setState(() {
            _status = 'Proof received!';
            _proofData =
                'Extracted data: $\{proof.claimData.context}, Full proof: $\{proof.toString()}';
          });
        }
      
        void _handleProofError(Exception error) {
          _handleError('Error in proof generation', error);
        }
      
        void _handleError(String message, dynamic error) {
          print('$message: $error');
          setState(() => _status = '$message: $\{error.toString()}');
        }
      
        Future<ReclaimProofRequest> _initializeProofRequest() async {
          final reclaimProofRequest = await ReclaimProofRequest.init(
            "YOUR_APP_ID", // Replace with your actual Application ID
            "YOUR_APP_SECRET", // Replace with your actual Application Secret
            "YOUR_PROVIDER_ID", // Replace with your actual Provider ID
          );
          return reclaimProofRequest;
        }
      
        Future<String> _generateRequestUrl(ReclaimProofRequest request) async {
          final requestUrl = await request.getRequestUrl();
          print('Request URL: $requestUrl');
          return requestUrl;
        }
      
        Future<void> _launchUrl(String url) async {
          if (await canLaunchUrl(Uri.parse(url))) {
            final launched = await launchUrl(
              Uri.parse(url),
              mode: LaunchMode.externalApplication,
            );
            if (launched) {
              setState(() => _status = 'Session started. Waiting for proof...');
            } else {
              throw 'Could not launch $url';
            }
          }
        }
      
        Future<void> startReclaimSession() async {
          try {
            print('Starting Reclaim session');
            final reclaimProofRequest = await _initializeProofRequest();
            final requestUrl = await _generateRequestUrl(reclaimProofRequest);
            await _launchUrl(requestUrl);
            await _startVerificationSession(reclaimProofRequest);
          } catch (error) {
            _handleError('Error starting Reclaim session', error);
          }
        }
      
        @override
        Widget build(BuildContext context) {
          return Scaffold(
            appBar: AppBar(title: const Text('Reclaim SDK Demo')),
            body: Padding(
              padding: const EdgeInsets.all(16.0),
              child: Column(
                mainAxisAlignment: MainAxisAlignment.center,
                crossAxisAlignment: CrossAxisAlignment.stretch,
                children: [
                  ElevatedButton(
                    onPressed: startReclaimSession,
                    child: const Text('Start Reclaim Session'),
                  ),
                  const SizedBox(height: 20),
                  Text(_status, style: const TextStyle(fontWeight: FontWeight.bold)),
                  const SizedBox(height: 20),
                  if (_proofData.isNotEmpty)
                    Expanded(
                      child: SingleChildScrollView(
                        child: Text(_proofData),
                      ),
                    ),
                ],
              ),
            ),
          );
        }
      }
`;

      case 'Node':
        return `
        const express = require('express')
        const { ReclaimProofRequest } = require('@reclaimprotocol/js-sdk')
        
        const app = express()
        const port = 3000
        
        app.use(express.json())
        
        app.get('/reclaim/generate-config', async (req, res) => {
          const APP_ID = 'YOUR_APPLICATION_ID'
          const APP_SECRET = 'YOUR_APPLICATION_SECRET'
          const PROVIDER_ID = 'YOUR_PROVIDER_ID'
        
          try {
            const reclaimProofRequest = await ReclaimProofRequest.init(APP_ID, APP_SECRET, PROVIDER_ID)
            
            reclaimProofRequest.setAppCallbackUrl('https://your-backend.com/receive-proofs')
            
            const reclaimProofRequestConfig = reclaimProofRequest.toJsonString()
        
            return res.json({ reclaimProofRequestConfig })
          } catch (error) {
            console.error('Error generating request config:', error)
            return res.status(500).json({ error: 'Failed to generate request config' })
          }
        })
        
        app.post('/receive-proofs', (req, res) => {
          const proofs = req.body
          console.log('Received proofs:', proofs)
          // Process the proofs here
          return res.sendStatus(200)
        })
        
        app.listen(port, () => {
          console.log('Server running at http://localhost:' + port)
        })
        `;
      case 'Solidity':
        return `
      
// SPDX-License-Identifier: MIT
pragma solidity 0.8.20;
 
 
import "@reclaimprotocol/verifier-solidity-sdk/contracts/Reclaim.sol";
import "@reclaimprotocol/verifier-solidity-sdk/contracts/Addresses.sol";
 
 
contract Attestor {
   address public reclaimAddress;
   // add providersHashes for your permitted providers
   string[] public providersHashes;
 
   constructor(string[] memory _providersHashes){
      providersHashes = _providersHashes;
        // TODO: Replace with network you are deploying on
      reclaimAddress = Addresses.PLOYGON_MUMBAI_TESTNET; 
 
   }  
 
   function verifyProof(Reclaim.Proof memory proof) public view{
       Reclaim(reclaimAddress).verifyProof(proof, providersHashes);
       // TODO: your business logic upon success
       // verify proof.context is what you expect
   }
}
      
`;
      case 'Solana':
        return `use anchor_lang::prelude::*;
use anchor_lang::prelude::*;

use reclaim::cpi::accounts::VerifyProof;
use reclaim::cpi::verify_proof;
use reclaim::instructions::VerifyProofArgs;
use reclaim::program::Reclaim;
use reclaim::state::ClaimData as ReclaimClaimData;
use reclaim::state::ClaimInfo as ReclaimClaimInfo;
use reclaim::state::SignedClaim as ReclaimSignedClaim;
use reclaim::state::{Epoch, EpochConfig};

declare_id!("your program id here");

#[derive(AnchorSerialize, AnchorDeserialize)]
pub struct VerifyArgs {
    pub claim_info: ClaimInfo,
    pub signed_claim: SignedClaim,
}

#[derive(AnchorSerialize, AnchorDeserialize, Clone)]
pub struct SignedClaim {
    pub claim_data: ClaimData,
    pub signatures: Vec<[u8; 65]>,
}

#[derive(AnchorSerialize, AnchorDeserialize, Clone)]
pub struct ClaimInfo {
    pub provider: String,
    pub parameters: String,
    pub context_address: Pubkey,
    pub context_message: String,
}

#[derive(AnchorSerialize, AnchorDeserialize, Clone)]
pub struct ClaimData {
    pub identifier: [u8; 32],
    pub owner: String,
    pub timestamp: u32,
    pub epoch_index: u32,
}

#[program]
pub mod base {
    use super::*;

    pub fn verify<'info>(
        ctx: Context<'_, '_, '_, 'info, Verify<'info>>,
        args: VerifyArgs,
    ) -> Result<()> {
        let VerifyArgs {
            claim_info,
            signed_claim,
        } = args;

        let signer_account_info = ctx.accounts.signer.to_account_info();
        let reclaim_program_info = ctx.accounts.reclaim_program.to_account_info();

        let epoch_config_account_info = ctx.accounts.epoch_config.to_account_info();
        let epoch_account_info = ctx.accounts.epoch.to_account_info();

        verify_proof(
            CpiContext::new(
                reclaim_program_info,
                VerifyProof {
                    epoch_config: epoch_config_account_info,
                    epoch: epoch_account_info,
                    signer: signer_account_info,
                },
            ),
            VerifyProofArgs {
                claim_info: ReclaimClaimInfo {
                    parameters: claim_info.parameters,
                    context_message: claim_info.context_message,
                    provider: claim_info.provider,
                    context_address: claim_info.context_address,
                },
                signed_claim: ReclaimSignedClaim {
                    claim_data: ReclaimClaimData {
                        epoch_index: signed_claim.claim_data.epoch_index,
                        timestamp: signed_claim.claim_data.timestamp,
                        identifier: signed_claim.claim_data.identifier,
                        owner: signed_claim.claim_data.owner,
                    },
                    signatures: signed_claim.signatures,
                },
            },
        )?;

        Ok(())
    }
}

#[derive(Accounts)]
pub struct Verify<'info> {
    #[account(mut)]
    pub signer: Signer<'info>,
    pub epoch_config: Account<'info, EpochConfig>,
    pub epoch: Account<'info, Epoch>,
    pub reclaim_program: Program<'info, Reclaim>,
    pub system_program: Program<'info, System>,
}	
`;

      default:
        return '// Default code here';
    }
  };

  const toast = useToast();
  const navigation = useNavigate();
  const auth = getAuth(app);
  const dispatch = useAppDispatch();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        navigation('/dashboard');
      }
    });

    // Clean up the listener when the component unmounts
    return () => unsubscribe();
  }, [auth, navigation]);

  async function handleGoogleLogin() {
    const provider = new GoogleAuthProvider();
    try {
      const user = await signInWithPopup(auth, provider, browserPopupRedirectResolver);

      if (user) {
        dispatch(loginClient())
          .then((res: any) => {
            if (res.meta.requestStatus === 'fulfilled') {
              dispatch(setUserType(res.payload.type));
              toast({
                title: 'Welcome back',
                description: 'You have been logged in successfully',
                status: 'success',
                duration: 3000,
                isClosable: true,
                position: 'top-right',
              });
            } else {
              toast({
                title: 'Error',
                description: res.payload as string,
                status: 'error',
                duration: 3000,
                isClosable: true,
                position: 'top-right',
              });
            }
          })
          .catch((err: any) => {
            toast({
              title: 'Error',
              description: err.message,
              status: 'error',
              duration: 3000,
              isClosable: true,
              position: 'top-right',
            });
          });
      }
    } catch (e: any) {
      toast({
        title: e?.message.replace('Firebase: ', ''),
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });
    }
  }

  return (
    <Box padding="96px 24px">
      <Container>
        <Heading
          as="h1"
          fontWeight="bold"
          textAlign="center"
          color="white"
          fontSize="40px"
        >
          Integrate Reclaim Into Your App Now
        </Heading>

        <Box mt={pxToRem(30)} mb={4} display="flex" justifyContent="center">
          {techIcons.map((techObj) => (
            <Box
              key={techObj.name}
              mx={4}
              fontSize="lg"
              color={selectedTech === techObj.name ? 'teal.500' : 'white'}
              cursor="pointer"
              onClick={() => handleTechClick(techObj.name)}
              display="flex"
              alignItems="center"
            >
              {['Flutter', 'Solidity', 'Solana'].includes(techObj.name) ? (
                <Image src={techObj.icon} alt={techObj.name} boxSize={8} mr={2} />
              ) : (
                <Icon as={techObj.icon} boxSize={8} mr={2} />
              )}

              <Box color={selectedTech === techObj.name ? '#3385ff' : 'white'}>
                {techObj.name}
              </Box>
            </Box>
          ))}
        </Box>

        <Box margin="0 auto">
          <CodeBlock language="javascript" code={getCodeSnippet()} framework="" />
        </Box>
      </Container>

      <Center mt={pxToRem(24)}>
        <Button
          onClick={handleGoogleLogin}
          colorScheme="blue"
          fontSize={pxToRem(20)}
          size="lg"
          color="#fff"
        >
          Start Building for Free
        </Button>
      </Center>
    </Box>
  );
};
